import React, { useEffect, useMemo, useRef, useState } from 'react';
import FormError from '../feedback/FormError';
import { toast } from 'react-hot-toast';
import utils from '../../utils';
import adminServices from '../../services/AdminServices';
import UserForm from '../dashboard/UserForm';
import { HiTrash } from 'react-icons/hi';
import Select from 'react-select';
import { useAdminData } from '../../context/SessionContext';
import LocationForm from '../dashboard/LocationForm';
import { useLoadScript } from '@react-google-maps/api'
import LocationModal from '../dashboard/LocationModal';

const libraries = ["places"];

function NurseModal({ action, activeUser, onReload, setActiveUser }) {
    const [loading, setLoading] = useState(false);
    const [createdUser, setCreatedUser] = useState()
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [location, setLocation] = useState()

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
        id: 'google-maps-script',
        async: true,
        defer: true,
    });

    const [licence, setLicence] = useState('');
    const [yearsOfExperience, setYearsOfExperience] = useState('');

    const fileInputRef = useRef();
    const [image, setImage] = useState(null);
    const [selectedSkills, setSelectedSkills] = useState([]);

    const { services } = useAdminData();

    const skillOptions = useMemo(() => {
        return (services || []).map((skill) => ({
            value: skill.id,
            label: skill.name,
        }));
    }, [services]);

    const tabs = useMemo(() => {
        const baseTabs = ['Account', 'Nurse',];
        if (action === 1) return [...baseTabs, 'Delete'];
        return baseTabs;
    }, [action]);


    const clearData = () => {
        setActiveTab(0);
        setImage(null);
        setSelectedSkills([]);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setActiveUser?.(null);
    };

    useEffect(() => { }, [createdUser])

    useEffect(() => { }, [activeUser])

    useEffect(() => {
        if (action === 1 && activeUser?.id) {
            setLicence(activeUser.license_number || '');
            setYearsOfExperience(activeUser.years_of_experience || '');
            if (activeUser.skills) {
                const preselected = activeUser.skills.map(skill => ({
                    value: skill.id,
                    label: skill.name,
                }));
                setSelectedSkills(preselected);
            }
            if (activeUser?.location) {
                setLocation(activeUser?.location)
            }
        } else {
            setLicence('');
            setYearsOfExperience('');
            setSelectedSkills([]);
            setLocation()
        }
    }, [action, activeUser]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && !file.type.startsWith('image/')) {
            toast.error('Please select a valid image file.');
            e.target.value = '';
            return;
        }
        setImage(file);
    };

    // Submit nurse details
    const handleNurseSubmit = (e) => {
        e.preventDefault();
        const closeBtn = document.getElementById('nurse-create-modal-close-btn');
        setError(null);
        setLoading(true);

        const formData = new FormData();
        formData.append('license_number', licence);
        formData.append('years_of_experience', yearsOfExperience);

        const skillIds = selectedSkills.map((opt) => opt.value);
        skillIds.forEach((id) => {
            formData.append('skills', id);
        });

        if (image) {
            formData.append('document', image);
        }

        let request;

        if (action === 1) {
            const origLoc = activeUser?.location;
            const hasLocationChanged =
                !origLoc ||
                location.address !== origLoc.address ||
                location.city !== origLoc.city ||
                location.county !== origLoc.county;

            const nurseUpdatePromise = () => adminServices.updateNurse(activeUser.id, formData);

            if (!hasLocationChanged) {
                request = nurseUpdatePromise();
            } else {
                if (origLoc?.id) {
                    request = adminServices
                        .updateLocation(origLoc.id, {
                            address: location.address,
                            city: location.city,
                            county: location.county,
                            lat: location.lat,
                            lng: location.lng,
                        })
                        .then(() => nurseUpdatePromise()); // 
                } else {
                    request = adminServices
                        .addLocation({
                            address: location.address,
                            city: location.city,
                            county: location.county,
                            lat: location.lat,
                            lng: location.lng,
                        })
                        .then(locRes => {
                            formData.append('location', locRes.data.id);
                            return nurseUpdatePromise();
                        });
                }
            }
        } else {
            if (!createdUser?.id) {
                toast.error("User must be created first");
                setLoading(false);
                return;
            }
            formData.append("user", createdUser.id);

            const locationToast = toast.loading("Creating location...");

            request = adminServices
                .addLocation(location)
                .then((res) => {
                    toast.success("Location created", { id: locationToast });
                    formData.append("location", res.data?.id);

                    return adminServices.addNurse(formData);
                })
                .catch((err) => {
                    toast.error(utils.toastError, { id: locationToast });
                    throw err;
                });
        }

        request
            .then((res) => {
                toast.success(
                    action === 1
                        ? 'Nurse updated successfully'
                        : 'Nurse profile created successfully'
                );
                setLoading(false);
                onReload?.();
                closeBtn?.click();
            })
            .catch((err) => {
                setLoading(false);
                console.error("Submission error:", err);
                const errorMsg = err.response?.data || err.message || utils.toastError;
                setError(errorMsg);
                toast.error(typeof errorMsg === 'string' ? errorMsg : utils.toastError);
            });
    };

    // Delete handler
    const handleDelete = () => {
        const closeBtn = document.getElementById('nurse-create-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deleteNurse(activeUser.id)
            .then((res) => {
                toast.success('Nurse deleted successfully');
                setDeleteLoading(false);
                onReload?.();
                closeBtn?.click();
            })
            .catch((err) => {
                setDeleteLoading(false);
                toast.error(utils.toastError);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    const handleLocationSuccess = (locationData) => {
        // console.log(locationData);
        
        setLocation(locationData)
    };

    const handleUserSuccess = (data) => {
        if (action === 1) {
            onReload?.();
        }
        setCreatedUser(data)
        setActiveTab(1)
    }

    return (
        <div>
            <label htmlFor="nurse-create-modal" id="nurse-create-open-btn" className="hidden">
                Add / Update Nurse
            </label>
            <input type="checkbox" id="nurse-create-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-8/11 max-w-3xl">
                    <label
                        onClick={clearData}
                        htmlFor="nurse-create-modal"
                        id="nurse-create-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    {(action === 1) && (activeUser?.location?.lat && activeUser?.location?.lng) ? (
                        <LocationModal
                            activeUser={activeUser}
                            lat={activeUser?.location?.lat}
                            lng={activeUser?.location?.lng} />
                    ) : null}

                    <h2 className="text-lg font-bold mb-2">
                        {action === 0 ? 'Add' : 'Edit'} Nurse
                        {action === 1 && activeUser && (
                            <span className="text-gray-500 ml-2">{activeUser?.user?.full_name || activeUser?.user?.email}</span>
                        )}
                    </h2>

                    {/* Tabs */}
                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx ? 'tab-active bg-primary text-white rounded-lg' : ''}`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    {activeTab === 0 && (
                        <div>
                            <UserForm
                                activeUser={activeUser}
                                action={action}
                                role="Nurse"
                                onSuccess={handleUserSuccess}
                            />
                        </div>
                    )}

                    {activeTab === 1 && (
                        <form onSubmit={handleNurseSubmit} className='space-y-3'>
                            <h4 className=' text-lg font-bold'>Nurse Info:</h4>
                            {action === 1 && activeUser && (
                                <div className='flex flex-col gap-4 md:flex-row items-center'>
                                    <div className='flex-grow-0'>
                                        {activeUser?.document && (
                                            <div>
                                                <a target='_blank' href={activeUser?.document} className='hover:cursor-pointer' rel="noreferrer">
                                                    <img
                                                        className='h-44 rounded-lg'
                                                        src={activeUser?.document}
                                                        alt="document" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    <div className="border flex-grow w-full border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-3 shadow-sm transition-all hover:shadow-md rounded-xl">
                                        <div className="space-y-2 text-sm">
                                            <p className="font-bold">Overview</p>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <p className='md:col-span-2'>
                                                    <span className="font-bold mr-2">License Number:</span>
                                                    {activeUser?.license_number || '—'}
                                                </p>
                                                <p className='md:col-span-2'>
                                                    <span className="font-bold mr-2">Years of Experience:</span> {activeUser?.years_of_experience}
                                                </p>
                                                <p className='md:col-span-2'>
                                                    <span className="font-bold mr-2">Address:</span> {activeUser?.location?.address || activeUser?.location?.city || '--'}
                                                </p>
                                                <p>
                                                    <span className="font-bold mr-2">Ratings:</span> {0}
                                                </p>
                                                <p>
                                                    <span className="font-bold mr-2">Total Request:</span> {activeUser?.request_count}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <FormError error={error} />

                            <div className="form-control ">
                                <label className="label">
                                    <span className="label-text">Licence Number *</span>
                                </label>
                                <input
                                    type="text"
                                    value={licence}
                                    required
                                    onChange={(e) => setLicence(e.target.value)}
                                    className="input input-bordered w-full"
                                    placeholder="Enter licence number"
                                />
                            </div>

                            <div className="form-control ">
                                <label className="label">
                                    <span className="label-text">Years of Experience *</span>
                                </label>
                                <input
                                    type="text"
                                    value={yearsOfExperience}
                                    required
                                    onChange={(e) => setYearsOfExperience(e.target.value)}
                                    className="input input-bordered w-full"
                                    placeholder="Enter licence number"
                                />
                            </div>

                            <div className="form-control ">
                                <label className="label">
                                    <span className="label-text">Specializations (Skills) *</span>
                                </label>
                                <Select
                                    isMulti
                                    value={selectedSkills}
                                    onChange={setSelectedSkills}
                                    options={skillOptions}
                                    placeholder="Select one or more skills..."
                                    noOptionsMessage={() => "No skills available. Add categories first."}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            borderColor: '#d1d5db',
                                            '&:hover': { borderColor: '#9ca3af' },
                                        }),
                                    }}
                                />
                            </div>

                            <div className="form-control ">
                                <label className="label">
                                    <span className="label-text">
                                        {action === 0 ? 'Document File *' : 'Document File (Optional)'}
                                    </span>
                                </label>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*,.pdf"
                                    onChange={handleFileChange}
                                    className="file-input file-input-bordered w-full"
                                    required={action === 0}
                                />
                            </div>

                            <div className="pt-2">
                                <div className="divider"></div>
                            </div>

                            {isLoaded && (
                                <LocationForm
                                    activeUser={activeUser}
                                    location={location}
                                    action={action}
                                    onSuccess={handleLocationSuccess}
                                />
                            )}

                            <div className="">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                >
                                    {loading ? 'Saving...' : 'Save Nurse Profile'}
                                </button>
                            </div>
                        </form>
                    )}
                    {activeTab === 2 && action === 1 && (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                        Deleting this nurse will remove their account and all associated data.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete: {activeUser?.user?.full_name || activeUser?.user?.email}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default NurseModal;